import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface Week {
  id: string;
  week_number: number;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  color: string;
  display_order: number;
}

export default function WeeksSection() {
  const [weeks, setWeeks] = useState<Week[]>([]);

  useEffect(() => {
    async function fetchWeeks() {
      try {
        const { data, error } = await supabase
          .from('home_weeks')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (error) throw error;
        if (data) setWeeks(data);
      } catch (error) {
        console.error('Error fetching weeks:', error);
      }
    }

    fetchWeeks();
  }, []);

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      yellow: 'from-yellow-600 to-yellow-700',
      amber: 'from-amber-600 to-yellow-600',
      orange: 'from-orange-600 to-yellow-600'
    };
    return colorMap[color] || 'from-yellow-600 to-yellow-700';
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Votre site en <span className="text-yellow-600">3 semaines</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Un processus clair et efficace pour un résultat professionnel
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {weeks.map((week, index) => (
            <div 
              key={week.id}
              className="relative group"
            >
              {/* Connector Line */}
              {index < weeks.length - 1 && (
                <div className="hidden md:block absolute top-20 left-full w-full h-0.5 bg-gradient-to-r from-yellow-600/30 to-transparent z-0"></div>
              )}

              <div className="relative bg-slate-50 rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 h-full border-2 border-transparent hover:border-yellow-600/20">
                {/* Week Number Badge */}
                <div className={`absolute -top-6 left-8 w-12 h-12 flex items-center justify-center bg-gradient-to-br ${getColorClasses(week.color)} rounded-full shadow-lg`}>
                  <span className="text-white font-bold text-xl">{week.week_number}</span>
                </div>

                {/* Title with Icon */}
                <div className="flex items-center mt-4 mb-2">
                  <i className={`${week.icon} text-2xl text-yellow-600 mr-3`}></i>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {week.title}
                  </h3>
                </div>
                
                <p className="text-yellow-700 font-semibold mb-6">
                  {week.subtitle}
                </p>

                {/* Description */}
                <p className="text-slate-700 leading-relaxed">
                  {week.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-2 bg-yellow-50 text-yellow-800 px-6 py-3 rounded-full">
            <i className="ri-time-line text-xl"></i>
            <span className="font-semibold">Votre site professionnel en seulement 3 semaines</span>
          </div>
        </div>
      </div>
    </section>
  );
}
